/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useContext, useEffect, useState } from "react";
import { buildURLForMenuItem } from "../../../../utils/buildURL";
import Button from "../../../Button";
import { LanguageContext, text } from "../../../../container/language";
import data from "../../data";
import { useTranslation } from "react-i18next";
import { usePageContext } from "../../../../utils/PageContext";
import { Link as GatsbyLink, useStaticQuery } from "gatsby";
import Link from "../../../../utils/Link";
import clsx from "clsx";
import { buildURLForArticle } from "../../../../utils/buildURL";
import { globalHistory } from "@reach/router";
import { useMediaQuery } from "beautiful-react-hooks";

const MAX_MENU_ITEMS = 5;

export const useCollapsible = (base = [], active) => {
  let classes = {};
  const [isClicked, setClicked] = useState(false);

  base.forEach((baseClass) => (classes[baseClass] = true));

  classes[active] = isClicked;

  const toggle = (val = !isClicked) => {
    setClicked(val);
  };

  const show = () => {
    setClicked(true);
  };

  const hide = () => {
    setClicked(false);
  };

  useEffect(() => {}, []);

  return { toggle, classes: clsx(classes), show, hide };
};

const useCollapsibleList = (base = [], active, num) => {
  const state = new Array(num)
    .fill("")
    .map((item, index) => useCollapsible(base, active));

  const getItem = (index) => {
    return state[index];
  };

  const toggle = (index) => {
    state.forEach((i) => i.toggle(false));

    state[index].toggle();
  };

  const show = (index) => {
    state.forEach((i) => i.toggle(false));

    state[index].toggle(true);
  };

  const hide = (index) => {
    state.forEach((i) => i.toggle(false));

    state[index].toggle(false);
  };

  const hideAll = (index) => {
    state.forEach((i) => i.toggle(false));
  };

  return { getItem, toggle, show, hide, hideAll };
};

export const query = graphql`
  fragment Category on API_Categories {
    id
    slug
  }

  fragment ArticleLink on API_Article {
    slug
    category {
      ...Category
      slug
      title {
        id
        text_en
        text_sq
      }
      parentCategory {
        ...Category
        slug
        title {
          id
          text_en
          text_sq
        }
        parentCategory {
          ...Category
          slug
          title {
            id
            text_en
            text_sq
          }
          parentCategory {
            ...Category
            title {
              id
              text_en
              text_sq
            }
            slug
            parentCategory {
              ...Category
              slug
              title {
                id
                text_en
                text_sq
              }
            }
          }
        }
      }
    }
  }

  query {
    api {
      configContact {
        phoneNumber
        tollFree
        email
      }
      configSelectedArticle {
        loanArticle {
          ...ArticleLink
        }
        cardsArticle {
          ...ArticleLink
        }
        myFibankArticle {
          ...ArticleLink
        }
      }
    }
  }
`;

export default function CategoriesNav({
  onToggle,
  isExpanded,
  onClose,
  categories: clientCategories,
  businessCategories,
  aboutUsCategories,
}) {
  const { t } = useTranslation();
  const { lang, articleType } = usePageContext();

  const isBusiness = articleType === "BUSINESS";
  const isAboutUs = articleType === "ABOUT_US";
  const isPersonal = !isBusiness && !isAboutUs;

  const apiData = useStaticQuery(query);
  const selectedArticles = apiData.api.configSelectedArticle;
  const configContact = apiData.api.configContact;

  const mediaMobile = useMediaQuery(
    "(min-width: 320px) and (max-width: 768px)"
  );

  const mediaTablet = useMediaQuery(
    "(min-width: 769px) and (max-width: 1024px)"
  );

  const categories =
    articleType === "BUSINESS"
      ? businessCategories
      : articleType === "ABOUT_US"
      ? aboutUsCategories
      : clientCategories;

  const languageToChangeTo = lang === "sq" ? "en" : "sq";

  const path = typeof window !== "undefined" ? window.location.pathname : "";

  const collapsibleMoreMenu = useCollapsible(
    ["main-nav__list-item", "main-nav__list-item--alt"],
    "main-nav__list-item--expanded"
  );

  const collapsibleMenu = useCollapsibleList(
    ["main-nav__list-item", "main-nav__list-item--has-dropdown"],
    "main-nav__list-item--expanded",
    5
  );

  const BUSINESS_LABEL = t("business");
  const PERSONAL_LABEL = t("personal");
  const ABOUT_US_LABEL = t("pages.about.label");

  const buttonMenus = [
    {
      text: t("buttonMenus.applyForLoan"),
      type: ["INDIVIDUAL"],
      icon: "i-credit",
      style: {},
      children: [
        {
          text: t("buttonMenus.loan"),
          icon: "i-credit",
          info: {
            text: t("buttonMenus.info"),
            link: buildURLForArticle(selectedArticles.loanArticle),
          },
          button: {
            text: t("buttonMenus.apply"),
            link: "/tools/loan-application",
          },
        },
        {
          text: t("buttonMenus.card"),
          icon: "i-credit",
          info: {
            text: t("buttonMenus.info"),
            link: buildURLForArticle(selectedArticles.cardsArticle),
          },
          button: {
            text: t("buttonMenus.apply"),
            link: "/tools/credit-card-application",
          },
        },
      ],
    },
    {
      text: t("buttonMenus.myFibank"),
      type: ["INDIVIDUAL", "BUSINESS"],
      icon: "i-monitor-heart-rate",
      style: {
        secondary: true,
      },
      children: [
        {
          text: t("buttonMenus.myFibank"),
          icon: "i-monitor-heart-rate",
          info: {
            text: t("buttonMenus.learnMore"),
            link: buildURLForArticle(selectedArticles.myFibankArticle),
          },
          button: {
            text: t("buttonMenus.login"),
            link: "https://e-banking.fibank.al/",
          },
        },
      ],
    },
  ];

  const menu = [
    {
      text: `${t("pages.contact.label")}`,
      link: "/contact",
      icon: "i-phone-alt",
    },
    {
      text: t("pages.branches.label"),
      link: "/branches",
      icon: "i-map-marker-alt",
    },
    {
      text: t("pages.about.label"),
      link: "/about",
      icon: "i-users-alt",
    },
    {
      text: t("pages.news.label"),
      link: "/lajme",
      icon: "i-newspaper",
    },
    {
      text: t("pages.help.label"),
      link: "/help",
      icon: "i-shield-question",
    },
  ];

  useEffect(() => {
    globalHistory.listen(({ action }) => {
      if (action === "PUSH") {
        collapsibleMenu.hideAll();
      }

      // if (isExpanded) {
      onClose();
      // }
    });
  }, []);

  const isTabletOrMobile = mediaMobile || mediaTablet;

  return (
    <div className="header-wrapper__menu main-header__nav">
      <nav className="main-nav main-nav--visible">
        <div className="main-nav__menu-scrollbar">
          <div className="viewport">
            <div
            // className="overview"
            // style={{ paddingLeft: "calc((50vw - 16px) - 0px); left: 0px;" }}
            >
              <ul className="main-nav__menu">
                <li className="main-nav__menu-item">
                  <Link to="/" className={isPersonal ? "active" : ""}>
                    {PERSONAL_LABEL}
                  </Link>
                </li>
                <li className="main-nav__menu-item">
                  <Link to="/business" className={isBusiness ? "active" : ""}>
                    {BUSINESS_LABEL}
                  </Link>
                </li>
                <li className="main-nav__menu-item">
                  <Link to="/about" className={isAboutUs ? "active" : ""}>
                    {ABOUT_US_LABEL}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <ul className="main-nav__list">
          {categories
            .slice(0, MAX_MENU_ITEMS)
            .map(({ label, image, columns }, index) => {
              const collapsibleMenuItem = collapsibleMenu.getItem(index);

              return (
                <li key={index} className={collapsibleMenuItem.classes}>
                  <div className="main-nav__link-wrap">
                    <Link
                      to={buildURLForMenuItem(label)}
                      className="main-nav__link"
                      onMouseEnter={() => {
                        if (!isTabletOrMobile) {
                          collapsibleMenu.show(index);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isTabletOrMobile) {
                          collapsibleMenu.hide(index);
                        }
                      }}
                      onClick={(event) => {
                        if (isTabletOrMobile) {
                          event.stopPropagation();
                          event.preventDefault();
                          collapsibleMenu.toggle(index);
                        }
                      }}
                    >
                      {text(label.label, lang)}
                    </Link>

                    <button
                      type="button"
                      className="main-nav__link-arrow js-main-nav__dropdown"
                    />
                  </div>

                  {columns.length ? (
                    <div
                      className="main-nav__dropdown"
                      // onClick={() => collapsibleMenu.toggle(index)}
                      onMouseEnter={() => collapsibleMenu.show(index)}
                      onMouseLeave={() => collapsibleMenu.hide(index)}
                    >
                      <div className="main-nav__dropdown-banner">
                        <div
                          className="banner-placeholder"
                          data-banner-image={image.url}
                        >
                          <Link to={buildURLForMenuItem(label)}>
                            <img src={image.url} alt="" />
                          </Link>
                        </div>
                      </div>
                      <div className="main-nav__dropdown-columns__holder">
                        <div className="main-nav__dropdown-columns">
                          {columns.map(({ head, body = [] }) => (
                            <div className="main-nav__dropdown-columns__item">
                              <div className="main-nav__dropdown-columns__inner">
                                <div className="main-nav__dropdown-columns__title">
                                  <Link to={buildURLForMenuItem(head)}>
                                    {text(head && head.label, lang)}
                                  </Link>
                                </div>
                                <ul className="main-nav__dropdown-columns__list">
                                  {body.map((item) => (
                                    <li className="main-nav__dropdown-columns__list-item">
                                      <Link to={buildURLForMenuItem(item)}>
                                        {text(item.label, lang)}
                                      </Link>
                                    </li>
                                  ))}
                                  <li className="main-nav__dropdown-columns__list-item">
                                    <Link
                                      to={buildURLForMenuItem(head)}
                                      className="more"
                                    >
                                      {t("menu.seeAll")}
                                    </Link>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}

          {categories.length > MAX_MENU_ITEMS ? (
            <li className={collapsibleMoreMenu.classes}>
              <div className="main-nav__link-wrap">
                <button
                  type="button"
                  className="main-nav__button js-nav__dropdown"
                  onClick={() => collapsibleMoreMenu.toggle()}
                >
                  Më tepër
                </button>
              </div>

              <div className="main-nav__dropdown main-nav__dropdown--alt main-nav__dropdown--other">
                <div className="main-nav__dropdown-columns__holder">
                  <div className="main-nav__dropdown-columns">
                    {categories.slice(MAX_MENU_ITEMS).map(({ label }) => (
                      <div
                        className="main-nav__dropdown-columns__item"
                        key={text(label, lang)}
                      >
                        <div className="main-nav__dropdown-columns__inner">
                          <div className="main-nav__dropdown-columns__title">
                            <Link to={buildURLForMenuItem(label)}>
                              {text(label.label, lang)}
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </li>
          ) : null}

          <li className="main-nav__list-item" data-width="378">
            <div className="header-wrapper__buttons">
              {buttonMenus.map(({ text, icon, style, children, type }) => {
                const collapsibleButton = useCollapsible(
                  ["button-wrap"],
                  "button-wrap--expanded"
                );

                if (!type.includes(articleType)) {
                  return null;
                }

                return (
                  <div className={collapsibleButton.classes}>
                    <Button
                      left
                      dropdown
                      {...style}
                      onClick={collapsibleButton.toggle}
                      onMouseEnter={collapsibleButton.show}
                      onMouseLeave={collapsibleButton.hide}
                    >
                      <i className={`icon ${icon}`} />
                      {text}
                      <div
                        dir="ltr"
                        className="resize-sensor"
                        style={{
                          pointerEvents: "none",
                          position: "absolute",
                          left: "0px",
                          top: "0px",
                          right: "0px",
                          bottom: "0px",
                          overflow: "hidden",
                          zIndex: "-1",
                          visibility: "hidden",
                          maxWidth: "100%",
                        }}
                      >
                        <div
                          className="resize-sensor-expand"
                          style={{
                            pointerEvents: "none",
                            position: "absolute",
                            left: "0px",
                            top: "0px",
                            right: "0px",
                            bottom: "0px",
                            overflow: "hidden",
                            zIndex: "-1",
                            visibility: "hidden",
                            maxWidth: "100%",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              left: "0px",
                              top: "0px",
                              transition: "all 0s ease 0s",
                              width: "158px",
                              height: "46px",
                            }}
                          />
                        </div>
                        <div
                          className="resize-sensor-shrink"
                          style={{
                            pointerEvents: "none",
                            position: "absolute",
                            left: "0px",
                            top: "0px",
                            right: "0px",
                            bottom: "0px",
                            overflow: "hidden",
                            zIndex: "-1",
                            visibility: "hidden",
                            maxWidth: "100%",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              left: "0px",
                              top: "0px",
                              transition: "all 0s ease 0s",
                              width: "200%",
                              height: "200%",
                            }}
                          />
                        </div>
                      </div>
                    </Button>

                    <div
                      className="button-dropdown"
                      onMouseEnter={collapsibleButton.show}
                      onMouseLeave={collapsibleButton.hide}
                    >
                      <ul className="button-dropdown__list">
                        {children.map(({ text, icon, info, button }) => (
                          <li className="button-dropdown__list-item">
                            <div className="button-dropdown__title">
                              <i className={icon} />
                              {text}
                            </div>
                            <div className="button-dropdown__buttons">
                              <Link to={info.link}>{info.text}</Link>
                              {button.link.startsWith("http") ? (
                                <a
                                  href={button.link}
                                  className="btn"
                                  target="_blank"
                                >
                                  {button.text}
                                </a>
                              ) : (
                                <Link to={button.link} className="btn">
                                  {button.text}
                                </Link>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </li>
        </ul>

        <div className="header-wrapper__box">
          <ul className="header-top__list header-top__list--alt">
            {menu.map(({ text, link, icon }) => (
              <li className="header-top__list-item">
                <Link to={link}>
                  <i className={`icon ${icon}`} />
                  {text}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>
      <div className="header-wrapper__menu-contacts">
        <p>
          <a href={`tel:${configContact.tollFree}`}>{configContact.tollFree}</a>
        </p>
        <p>
          <GatsbyLink
            to={path.startsWith("/en") ? path.replace(`/en`, ``) : `/en${path}`}
          >
            {languageToChangeTo === "sq" ? "AL" : "EN"}
          </GatsbyLink>
        </p>
      </div>
      <button
        type="button"
        className="header-wrapper__menu-button js-button__mobile-menu"
        onClick={() => onToggle()}
      >
        <span /> <span /> <span /> <span />
      </button>
    </div>
  );
}
